/**
 * useMessageSender — Core message sending, file upload, and response processing
 * Extracted VERBATIM from SimpleCopilotPanel.tsx
 * 
 * Contains:
 *   - processFilesOnly (L836-866) — file upload API call
 *   - processTextOnly (L868-878) — text query API call
 *   - handleSendMessage (L1028-1223) — main send callback
 *   - handlePromptSelected (L1243-1245) — set input text
 *   - handleRemoveMessage (L1247-1249) — remove message by ID
 *   - Initial prompt effect (L1225-1240) — auto-send deep-link prompt
 * 
 * Risks handled:
 *   - R1: Cross-hook state — all state received from orchestrator
 *   - R3: globalUploadedFiles — writes via globalUploadedFilesRef
 *   - R5: useCallback memoization — preserved exact dependency array
 *   - R14: currentActiveFormId — setter received from orchestrator
 *   - R20: useCallback identity — same deps, no forced splitting
 * 
 * SAFE REFACTORING: No logic changes. Code copied exactly as-is.
 * Two minor mechanical changes:
 *   1. createUserMessage now takes uploadedFiles as 3rd param (was closure)
 *   2. globalUploadedFiles writes go through globalUploadedFilesRef
 */

import * as React from 'react';
import { useRef } from 'react';
import { postExternalConsumptionAPI } from '../../../utils/httpUtilsExternalAPI';
import { COPILOT_CONSTANTS } from '../constants';
import mockApiResponse from '../DataSource/ApiResponseFormData.json';
import type { Message } from '../interfaces';
import type { CopilotChatParams } from './CopilotChatParams';
import { normalizeApiResponse } from '../utils/responseUtils';
import { createUserMessage, createBotMessage } from '../utils/messageUtils';
import { formatAndConvertByType } from '../utils/formatAndConvertByType';
import { extractTicketNumber } from '../utils/ticketExtraction';
import { selfHelpButtonShown, setSelfHelpButtonShown } from '../MessageTypes/MarkdownMessage';

/**
 * Hook for sending messages, uploading files, and processing API responses
 * Returns handleSendMessage, handlePromptSelected, handleRemoveMessage
 */
export function useMessageSender(params: CopilotChatParams) {
  const {
    messages,
    setMessages,
    loading,
    setLoading,
    setFollowUpQuestions,
    uploadedFiles,
    setUploadedFiles,
    showExitConfirmation,
    setShowExitConfirmation,
    setCurrentFormMetadata,
    setCurrentFormQuery,
    setCurrentActiveFormId,
    setCurrentTicketNumber,
    sessionIdRef,
    controlRef,
    scrollRef,
    lastAnchoredMessageIdRef,
    globalUploadedFilesRef,
    effectiveApi,
    userEmail,
    mcapsTelemetry,
    platformAuthClient,
    initialPrompt,
    onInitialPromptApplied,
  } = params;

  // Local ref for initial prompt guard (only used here)
  const hasAppliedInitialPromptRef = useRef(false);

  // Verbatim from SimpleCopilotPanel.tsx L836-866
  const processFilesOnly = async (files: File[], ticketId: string) => {
    if (!ticketId) {
      throw new Error('Ticket number not found to attach files.');
    }
    const fileUploadApiUrl = COPILOT_CONSTANTS.API.ENDPOINTS.FORM_ATTACHMENT;
    const apiPayload = new FormData();
    apiPayload.append('ticketId', ticketId);
    const appName = sessionStorage.getItem('mcapsCopilot_appName');
    if (appName) apiPayload.append('appName', appName);
    files.forEach((file) => {
      apiPayload.append('file', file, file.name);
    });
    const response = await postExternalConsumptionAPI(fileUploadApiUrl, apiPayload, platformAuthClient);
    
    // Normalize attachment response for chat display
    if (response && response.status >= COPILOT_CONSTANTS.API.SUCCESS_STATUS_MIN && response.status < COPILOT_CONSTANTS.API.SUCCESS_STATUS_MAX) {
      const rawPayload = response?.data ?? {};
      const successMsg = rawPayload.success 
        ? `✅ File "${rawPayload.fileName}" attached to ticket ${ticketId} successfully.`
        : `⚠️ Failed to attach file: ${rawPayload.validationError || 'Unknown error'}`;
      return {
        ...response,
        data: {
          ...rawPayload,
          response: successMsg,
          Message: successMsg,
          format: 'text'
        }
      };
    }
    return response;
  };

  // Story 1236552: Upload files to blob during conversational form-fill, then send text to /ask
  const processFormFillFiles = async (files: File[], question: string, isExitConfirmation: boolean) => {
    const attachmentUrl = COPILOT_CONSTANTS.API.ENDPOINTS.FORM_ATTACHMENT;
    const appName = sessionStorage.getItem('mcapsCopilot_appName');
    let uploadedCount = 0;
    let failedCount = 0;
    let blobFormInstanceId: string | null = null;

    for (const file of files) {
      const formPayload = new FormData();
      formPayload.append('file', file, file.name);
      formPayload.append('sessionId', sessionIdRef.current);
      formPayload.append('fieldId', 'uploadTemplate');
      if (appName) formPayload.append('appName', appName);
      try {
        const res = await postExternalConsumptionAPI(attachmentUrl, formPayload, platformAuthClient);
        if (res?.data?.success) {
          uploadedCount++;
          // Extract formInstanceId from blobReference (same as form mode)
          if (res.data.blobReference && !blobFormInstanceId) {
            blobFormInstanceId = res.data.blobReference.split('/').slice(-2, -1)[0];
          }
          console.log(`[Attachment] ✅ Conversational form-fill uploaded: ${file.name} | formInstanceId=${blobFormInstanceId}`);
        } else {
          failedCount++;
          console.warn(`[Attachment] ❌ Conversational form-fill failed: ${file.name}`);
        }
      } catch (err) {
        failedCount++;
        console.error(`[Attachment] Error uploading ${file.name}:`, err);
      }
    }

    // Send text + blob ref to /ask (matching form mode: append blob ref to query)
    if (question.trim()) {
      let queryWithBlob = question;
      if (blobFormInstanceId) {
        queryWithBlob = `${question},blob:${blobFormInstanceId}`;
      }
      const askAppName = sessionStorage.getItem('mcapsCopilot_appName');
      const apiPayload = {
        query: queryWithBlob,
        sessionId: sessionIdRef.current,
        ...(isExitConfirmation && { cancelForm: true }),
        ...(askAppName && { appName: askAppName })
      };
      const response = await postExternalConsumptionAPI(effectiveApi, apiPayload, platformAuthClient);
      return response;
    }

    // Files only, no text — return a synthetic success response
    return {
      status: 200,
      data: {
        response: uploadedCount > 0 ? `📎 ${uploadedCount} file(s) uploaded successfully.` : `⚠️ Failed to upload files.`,
        Message: uploadedCount > 0 ? `📎 ${uploadedCount} file(s) uploaded successfully.` : `⚠️ Failed to upload files.`,
        format: 'text'
      }
    };
  };

  // Verbatim from SimpleCopilotPanel.tsx L868-878
  const processTextOnly = async (question: string, cancelForm: boolean = false) => {
  const appName = sessionStorage.getItem('mcapsCopilot_appName');
  const apiPayload = {
    query: question,
    sessionId: sessionIdRef.current,
    ...(cancelForm && { cancelForm: true }),
    ...(appName && { appName: appName })
  };
    const response = await postExternalConsumptionAPI(effectiveApi, apiPayload, platformAuthClient);
    return response;
  };

  // Verbatim from SimpleCopilotPanel.tsx L1028-1223
  // R5: Exact useCallback wrapper preserved with same dependency array
  const handleSendMessage = React.useCallback(async (question: string) => {
    // WI-1249082: If self-help button was shown but user is sending a new message
    // instead of clicking "Create Incident", that's a deflection success
    if (selfHelpButtonShown === true) {
      setSelfHelpButtonShown(false);
      window.dispatchEvent(new CustomEvent('self-help-deflection-success', {
        detail: { nextQuery: question, timestamp: new Date().toISOString() }
      }));
    }

    setFollowUpQuestions([]);
    setLoading(true);
    
    // Check if this is an exit confirmation
    const isExitConfirmation = showExitConfirmation ;
    
    // Clear exit confirmation state after handling
    if (isExitConfirmation) {
      setShowExitConfirmation(false);
    }
    
    // Check if this is a manual reset query
    const trimmedQuestion = question.trim().toLowerCase();
    const isManualReset = trimmedQuestion === 'reset';
    
    const queryStartTime = Date.now();
    const hasFiles = uploadedFiles.length > 0;
    
    // Log telemetry
    mcapsTelemetry.logQuerySubmitted({
      query: question,
      component: 'SimpleCopilotPanel',
      action: 'submit'
    });

    // Add user message (uploadedFiles passed explicitly — was closure in original)
    const userMessage = createUserMessage(question, hasFiles, uploadedFiles);
    // Remember this as the active anchor question
    lastAnchoredMessageIdRef.current = userMessage.id;
    setMessages(prev => [...prev, userMessage]);

    try {
      let data: any;
      
      if (COPILOT_CONSTANTS.FEATURES.USE_MOCK_API) {
        data = mockApiResponse;
      } else {
        if (!effectiveApi) {
          throw new Error(COPILOT_CONSTANTS.API.ERROR_MESSAGES.API_NOT_CONFIGURED);
        }
        
        const ticketId = sessionStorage.getItem('mcapsCopilot_ticketNumber') ;
        let response: any;

        // Story 1236552: Check if conversational form session is active
        // formSessionActive comes from the latest bot message's meta
        const latestBotMsg = [...(messages || [])].reverse().find(m => m.role === 'assistant' && m.meta);
        const isFormSessionActive = (latestBotMsg?.meta as any)?.formSessionActive === true;

        // Route to appropriate API handler
        if (hasFiles) {
          if (isFormSessionActive && !ticketId) {
            // FORM-FILL MODE: Upload to blob via /attachment, then send text to /ask
            console.log('[Attachment] Routing to form-fill mode (conversational form session active)');
            response = await processFormFillFiles(uploadedFiles, question, isExitConfirmation);
          } else if (question.trim()) {
            if(ticketId){
                response = await processFilesOnly(uploadedFiles, ticketId);
            }else{
              setUploadedFiles([]);
              globalUploadedFilesRef.current = [];
              response = await processTextOnly(question, isExitConfirmation);
            }
             
          } else {
            if (!ticketId) {
              const errorMessage: Message = {
                id: COPILOT_CONSTANTS.MESSAGE_ID_PREFIXES.ERROR + Date.now(),
                role: 'assistant',
                content: 'Ticket number not found to attach files.',
                timestamp: Date.now()
              };
              setMessages(prev => [...prev, errorMessage]);
              setUploadedFiles([]);
              globalUploadedFilesRef.current = [];
              return;
            }
            response = await processFilesOnly(uploadedFiles, ticketId);
          }
        } else {
          response = await processTextOnly(question, isExitConfirmation);
        }

        if (!(response && response.status >= COPILOT_CONSTANTS.API.SUCCESS_STATUS_MIN && response.status < COPILOT_CONSTANTS.API.SUCCESS_STATUS_MAX)) {
          throw new Error('Something went wrong, Try again');
        }
        
        data = response.data || response;
      }

      // ========== RESPONSE NORMALIZATION ==========
      data = normalizeApiResponse(data, 'handleSendMessage');

      // ========== ADAPTIVE CARD DETECTION ==========
      // Check if this is a direct Adaptive Card response
      const isDirectAdaptiveCard = data && 
        typeof data === 'object' && 
        data.type === 'AdaptiveCard' && 
        data.version;
      let answerText: string;

      if (isDirectAdaptiveCard) {
        // Direct Adaptive Card response - no text content needed
        answerText = '';
      } else {
        // Regular response - extract text content
        answerText = data.Message || data.response || '';
      }

      const queryEndTime = Date.now();
      const processingTime = queryEndTime - queryStartTime;
      
      // Log response telemetry
      mcapsTelemetry.logResponseReceived({
        query: question,
        response: answerText || '[Adaptive Card Response]'
      });
      
      // Extract and store ticket number from message response
      const extractedTicketNumber = extractTicketNumber(answerText || JSON.stringify(data), data);
      if (extractedTicketNumber) {
        setCurrentTicketNumber(extractedTicketNumber);
        sessionStorage.setItem('mcapsCopilot_ticketNumber', extractedTicketNumber);
      } else {
      }
      
      // Format response and create bot message
      const formatted = formatAndConvertByType(data);
      const botMessage = createBotMessage(answerText, data, formatted);

      // Handle form metadata (skip for reset)
      if (!isManualReset && formatted.format === 'form' && formatted.dataToDisplay) {
        setCurrentFormMetadata(formatted.dataToDisplay);
        setCurrentFormQuery(question);
        setCurrentActiveFormId(botMessage.id);
      }

      // Story 1236552: Clear stale ticketId when a new form session starts
      // Prevents file uploads from attaching to a previous ticket
      if (data.formSessionActive && !extractedTicketNumber) {
        sessionStorage.removeItem('mcapsCopilot_ticketNumber');
        setCurrentTicketNumber(null);
      }

      // If this was a manual reset query, add separator directly (skip bot message)
      if (isManualReset) {
        setFollowUpQuestions([]); // Clear follow-up questions
        setTimeout(() => {
          const separatorMessage: Message = {
            id: COPILOT_CONSTANTS.MESSAGE_ID_PREFIXES.SYSTEM + Date.now(),
            role: 'separator',
            content: COPILOT_CONSTANTS.MESSAGES.NEW_CONVERSATION,
            timestamp: Date.now(),
            showNewConversation: true,
            separator: 'NewConversation',
            from: 'newConversation'
          };
          setMessages(prev => [...prev, separatorMessage]);
          
          // Scroll to bottom to show separator
          setTimeout(() => {
            if (scrollRef.current) {
              scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
            }
          }, 50);
        }, 100);
      } else {
        // Add bot message for normal queries
        setMessages(prev => [...prev, botMessage]);
      }
      
      // Update follow-up questions
      const newFollowUpQuestions = data.followUpQuestions || data.followupQuestions || [];
      setFollowUpQuestions(newFollowUpQuestions);

      // Clear uploaded files after successful response
      if (hasFiles) {        
        setUploadedFiles([]);
        globalUploadedFilesRef.current = [];
      }

    } catch (e: any) {
      console.error('API Error:', e);
      mcapsTelemetry.logError({
        query: question,
        error: e.message
      });

      // Use SystemIssue message like legacy implementation
      const errorMessage: Message = {
        id: COPILOT_CONSTANTS.MESSAGE_ID_PREFIXES.ERROR + Date.now(),
        role: 'error',
        content: COPILOT_CONSTANTS.MESSAGES.SYSTEM_ISSUE,
        timestamp: Date.now(),
        meta: {
          originalError: e.message,
          responseType: 'error'
        }
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
     
      setTimeout(() => {
        if (controlRef.current?.focus) {
          controlRef.current.focus();
        }
      }, 100);
    }
  }, [effectiveApi, userEmail, mcapsTelemetry, uploadedFiles, showExitConfirmation, messages]);

  // Verbatim from SimpleCopilotPanel.tsx L1225-1240
  // Apply an optional initial prompt (e.g., from deep-link) once by auto-submitting it
  React.useEffect(() => {
    if (!initialPrompt || hasAppliedInitialPromptRef.current) {
      return;
    }

    hasAppliedInitialPromptRef.current = true;
    handleSendMessage(initialPrompt);
    if (onInitialPromptApplied) {
      try {
        onInitialPromptApplied();
      } catch {
        // Fail-safe: ignore errors from external callback
      }
    }
  }, [initialPrompt, handleSendMessage]);

  // Listen for guidance prompt button clicks (send-prompt-action://)
  // Programmatically sends the prompt as if user typed it
  React.useEffect(() => {
    const handler = ((e: CustomEvent) => {
      const message = e.detail?.message;
      if (message && !loading) {
        handleSendMessage(message);
      }
    }) as EventListener;
    window.addEventListener('copilot-send-message', handler);
    return () => window.removeEventListener('copilot-send-message', handler);
  }, [handleSendMessage, loading]);

  // Verbatim from SimpleCopilotPanel.tsx L1243-1245
  const handlePromptSelected = React.useCallback((prompt: string) => {
    controlRef.current?.setInputText(prompt);
  }, []);

  // Verbatim from SimpleCopilotPanel.tsx L1247-1249
  const handleRemoveMessage = React.useCallback((messageId: string) => {
    setMessages(prev => prev.filter(msg => msg.id !== messageId));
  }, []);

  return {
    handleSendMessage,
    handlePromptSelected,
    handleRemoveMessage,
  };
}
